import { ErrorResponse, Purchase, ServiceResponse } from '@bzbs/react-api-client';
import { create } from 'zustand';
import { useBuzzebeesAppStore } from './buzzebeess-app.store';
import { useLocaleStore } from './locale.store';

export interface PurchaseConfig {
  defaultPageSize?: number;
  defaultConfig?: string;
}

export type PurchaseState = {
  data: Purchase[];
  isLoading: boolean;
  error: ErrorResponse | null;
  config: PurchaseConfig & { defaultPageSize: number };
  currentPage: number;
  hasMore: boolean;
  startDate?: string;
  endDate?: string;
};

export type PurchaseActions = {
  fetchData: (startDate?: string, endDate?: string) => Promise<ServiceResponse<Purchase[]>>;
  loadMore: () => Promise<ServiceResponse<Purchase[]>>;
  clear: () => void;
  updateConfig: (config: Partial<PurchaseConfig>) => void;
};

export type PurchaseStore = PurchaseState & PurchaseActions;

export const createPurchaseStore = (initialConfig: PurchaseConfig) => {
  return create<PurchaseStore>((set, get) => {
    return {
      data: [],
      isLoading: false,
      error: null,
      currentPage: 0,
      hasMore: true,
      startDate: undefined,
      endDate: undefined,
      config: {
        ...initialConfig,
        defaultPageSize: initialConfig.defaultPageSize || 10,
      },

      updateConfig: (newConfig: Partial<PurchaseConfig>) => {
        set((state) => ({
          config: {
            ...state.config,
            ...newConfig,
            defaultPageSize: newConfig.defaultPageSize || state.config.defaultPageSize,
          },
        }));
      },

      fetchData: async (startDate?: string, endDate?: string) => {
        const { config } = get();
        const bzbsService = useBuzzebeesAppStore.getState().bzbsService;

        if (!bzbsService) {
          return { type: 'client-error', message: 'Service not configured' };
        }

        // Always reset state when fetching with new date range
        set({
          currentPage: 0,
          data: [],
          hasMore: true,
          error: null,
          isLoading: true,
          startDate,
          endDate,
        });

        const response = await bzbsService.historyApi.redeemHistories({
          byConfig: true,
          config: config.defaultConfig || '',
          skip: 0,
          top: config.defaultPageSize,
          startDate,
          endDate,
          options: { device_locale: useLocaleStore.getState().localeId },
        });

        if (response.type === 'success') {
          set({
            data: response.model,
            hasMore: response.model.length === config.defaultPageSize,
            isLoading: false,
            error: null,
          });
        } else {
          set({ error: response, isLoading: false });
        }

        return response;
      },

      loadMore: async () => {
        const { config, data, currentPage, hasMore, startDate, endDate } = get();
        const bzbsService = useBuzzebeesAppStore.getState().bzbsService;

        if (!bzbsService) {
          return { type: 'client-error', message: 'Service not configured' };
        }

        if (!hasMore) {
          return { type: 'client-error', message: 'No more data to load' };
        }

        set({ isLoading: true, error: null });

        const response = await bzbsService.historyApi.redeemHistories({
          byConfig: true,
          config: config.defaultConfig || '',
          skip: currentPage * config.defaultPageSize,
          top: config.defaultPageSize,
          startDate,
          endDate,
          options: { device_locale: useLocaleStore.getState().localeId },
        });

        if (response.type === 'success') {
          set({
            data: [...data, ...response.model],
            hasMore: response.model.length === config.defaultPageSize,
            currentPage: currentPage + 1,
            isLoading: false,
            error: null,
          });
        } else {
          set({ error: response, isLoading: false });
        }

        return response;
      },

      clear: () => {
        set({
          data: [],
          isLoading: false,
          error: null,
          currentPage: 0,
          hasMore: true,
          startDate: undefined,
          endDate: undefined,
        });
      },
    };
  });
};
